import React from 'react';
import { Place } from '../types';

interface PlaceCardProps {
  place: Place;
  onSelect?: () => void;
  onDirections?: () => void;
  compact?: boolean;
  openingHours?: {
    open_now?: boolean;
    weekday_text?: string[];
  } | null;
}

export const PlaceCard: React.FC<PlaceCardProps> = ({
  place,
  onSelect,
  onDirections,
  compact = false,
  openingHours: _openingHours
}) => {
  const handleDirections = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDirections) {
      onDirections();
    } else {
      // Open in Google Maps
      const url = `https://www.google.com/maps/dir/?api=1&destination=${place.location.lat},${place.location.lng}&destination_place_id=${place.place_id}`;
      window.open(url, '_blank');
    }
  };

  if (compact) {
    return (
      <button
        onClick={onSelect}
        className="w-full bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-lg p-3 text-left transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900 truncate">{place.name}</h4>
            {place.formatted_address && (
              <p className="text-xs text-gray-500 truncate mt-0.5">{place.formatted_address}</p>
            )}
          </div>
          {place.rating && (
            <div className="flex items-center ml-2 text-xs">
              <span className="text-amber-500 flex items-center">
                {place.rating.toFixed(1)}
                <span className="material-symbols-outlined filled text-[14px] ml-0.5">star</span>
              </span>
            </div>
          )}
        </div>
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 card-hover">
      {/* Place Header */}
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="text-lg font-medium text-gray-900 truncate leading-snug">
              {place.name}
            </h3>
            <div className="flex items-center mt-1 text-sm text-gray-500">
              {place.rating && (
                <>
                  <span className="text-amber-500 flex items-center mr-1">
                    {place.rating.toFixed(1)}
                    <span className="material-symbols-outlined filled text-[14px] ml-0.5">star</span>
                  </span>
                  {place.user_ratings_total && (
                    <span className="text-gray-400">({place.user_ratings_total})</span>
                  )}
                  <span className="mx-1">•</span>
                </>
              )}
              {place.found_for_preference && (
                <span className="capitalize">{place.found_for_preference}</span>
              )}
            </div>
            {place.formatted_address && (
              <p className="text-sm text-gray-500 mt-1 truncate">{place.formatted_address}</p>
            )}
            {place.extra_time !== undefined && (
              <p className="text-xs text-blue-600 mt-1">
                +{place.extra_time} min detour
              </p>
            )}
          </div>
          {/* Place Icon */}
          <div className="w-14 h-14 bg-gray-100 rounded-lg flex-shrink-0 flex items-center justify-center">
            <span className="material-symbols-outlined text-gray-400 text-2xl">location_on</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2 mt-4 overflow-x-auto no-scrollbar">
          <button
            onClick={handleDirections}
            className="flex-shrink-0 bg-blue-600 active:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center shadow-sm transition-colors touch-feedback"
          >
            <span className="material-symbols-outlined text-[18px] mr-1.5">directions</span>
            Directions
          </button>
          {onSelect && (
            <button
              onClick={onSelect}
              className="flex-shrink-0 bg-white border border-gray-300 text-blue-600 px-4 py-2 rounded-full text-sm font-medium flex items-center active:bg-blue-50 transition-colors touch-feedback"
            >
              <span className="material-symbols-outlined text-[18px] mr-1.5">add_location</span>
              Add Stop
            </button>
          )}
          <button
            className="flex-shrink-0 bg-white border border-gray-300 text-gray-600 px-4 py-2 rounded-full text-sm font-medium flex items-center active:bg-gray-50 transition-colors touch-feedback"
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: place.name,
                  text: place.formatted_address || place.name,
                  url: `https://www.google.com/maps/place/?q=place_id:${place.place_id}`
                });
              }
            }}
          >
            <span className="material-symbols-outlined text-[18px] mr-1.5">share</span>
            Share
          </button>
        </div>
      </div>
    </div>
  );
};
